import { ChartConfigChartPaneElement, RequireByKey } from '../../types/shared'
import { IndicatorType } from '../constants/common'
import { MACDCalculation } from '../indicator-calculation/macd'
import { getBarWidthWithMarginByParts } from '../utils/chart'
import { drawInVisibleArea } from '../utils/draw_in_visible_area'
import { getParsedIntegersFromPeriodString } from '../utils/helpers'
import Indicator from './indicator'

interface Attrs {
  period: string
  fastPeriod: number
  slowPeriod: number
  signalPeriod: number
}

class MACD extends Indicator<Attrs> {
  static type = IndicatorType.Macd
  static label = 'MACD'

  static getNumOfBarsBuffer({ period }: RequireByKey<ChartConfigChartPaneElement, 'period'>) {
    const [fastPeriod = 0, slowPeriod = 0, signalPeriod = 0] = getParsedIntegersFromPeriodString(period)
    return Math.max(fastPeriod, slowPeriod, signalPeriod)
  }

  macdCalculation: MACDCalculation | null = null

  declare fastPeriod: number
  declare slowPeriod: number
  declare signalPeriod: number

  set(values: Partial<Attrs>) {
    if (typeof values.period === 'string') {
      const [fastPeriod = 0, slowPeriod = 0, signalPeriod = 0] = getParsedIntegersFromPeriodString(this.attrs.period)
      super.set({ ...values, fastPeriod, slowPeriod, signalPeriod })
    } else {
      super.set(values)
    }
  }

  compute() {
    if (this.isComputeNecessary() || this.macdCalculation === null) {
      this.macdCalculation = new MACDCalculation({
        quote: this.data,
        options: {
          signalPeriod: this.signalPeriod,
          fastPeriod: this.fastPeriod,
          slowPeriod: this.slowPeriod,
        },
      })
      this.macdCalculation.calculate()

      this.lastValue = this.macdCalculation.calculatedValues.macd.last() ?? null
    }

    const { macd, histogram, signal } = this.macdCalculation.calculatedValues

    const { min, max } =
      macd.length > 0 || signal.length > 0 || histogram.length > 0
        ? this.computeVisibleMinMax(macd, signal, histogram)
        : this.getDomainDefaults(this.type)
    this.min = min
    this.max = max
  }

  renderIndicator(context: CanvasRenderingContext2D) {
    const { IndicatorSettings } = this.getChartLayoutSettings()
    if (this.macdCalculation === null || this.data.close.length === 0) return
    const y = Math.round(this.fy(0))
    const chartModel = this.model.chart()
    const { zoomFactor } = chartModel
    const { barFillWidth, barBorderWidth } = getBarWidthWithMarginByParts({
      zoomFactor,
      chartLayout: chartModel.chart_layout(),
    })
    const barWidthWithoutMargin = barFillWidth + barBorderWidth * 2
    const halfBarWidth = ~~Math.max(1, barWidthWithoutMargin / 2)
    const { macd, histogram, signal } = this.macdCalculation.calculatedValues
    const drawInVisibleAreaProps = {
      quote: this.data,
      paneModel: this.model,
      leftOffset: this.leftOffset,
      width: this.width,
    }

    context.set('fillStyle', '#79c3e8')
    context.beginPath()
    drawInVisibleArea({
      ...drawInVisibleAreaProps,
      drawBarCallback: (i, x) => {
        context.rect(x - halfBarWidth, y, barWidthWithoutMargin, Math.round(this.fy(histogram[i]) - y))
      },
    })
    context.fill()

    context.translate(0.5, 0.5)
    context.set('strokeStyle', IndicatorSettings.general.Colors.line)
    context.beginPath()

    drawInVisibleArea({
      ...drawInVisibleAreaProps,
      drawBarCallback: (i, x) => {
        context.lineTo(x, Math.round(this.fy(macd[i])))
      },
    })
    context.stroke()

    context.set('strokeStyle', IndicatorSettings.general.Colors.signalLine)
    context.beginPath()
    drawInVisibleArea({
      ...drawInVisibleAreaProps,
      drawBarCallback: (i, x) => {
        context.lineTo(x, Math.round(this.fy(signal[i])))
      },
    })
    context.stroke()
    context.translate(-0.5, -0.5)
  }

  getModalConfig() {
    const fastPeriod = {
      type: 'number',
      label: 'Period',
      name: 'fastPeriod',
      value: this.fastPeriod ?? 12,
      required: true,
      min: 1,
      max: 999999,
    }
    const slowPeriod = {
      type: 'number',
      label: 'Slow',
      name: 'slowPeriod',
      value: this.slowPeriod ?? 26,
      required: true,
      min: 1,
      max: 999999,
    }
    const signalPeriod = {
      type: 'number',
      label: 'Signal',
      name: 'signalPeriod',
      value: this.signalPeriod ?? 9,
      required: true,
      min: 1,
      max: 999999,
    }

    return {
      title: this.label,
      inputs: [fastPeriod, slowPeriod, signalPeriod],
      inputsErrorMessages: {
        fastPeriod: `${fastPeriod.label} must be a whole number between ${fastPeriod.min} and ${fastPeriod.max}`,
        slowPeriod: `${slowPeriod.label} must be a whole number between ${slowPeriod.min} and ${slowPeriod.max}`,
        signalPeriod: `${signalPeriod.label} must be a whole number between ${signalPeriod.min} and ${signalPeriod.max}`,
      },
    }
  }

  getIsValid(key: string): boolean {
    switch (key) {
      case 'fastPeriod':
      case 'slowPeriod':
      case 'signalPeriod':
        return this.getIsNumberInputValid({ key })
      default:
        return false
    }
  }

  toString() {
    return `MACD ${this.fastPeriod},${this.slowPeriod},${this.signalPeriod}`
  }
}

export default MACD
